import { useEffect, useMemo, useRef, useState } from "react";
import {
  canViewTransaction,
  categoryFor,
  checklistPhaseCompletion,
  dateVerification,
  formatDate,
  formatDateTime,
  isApproved,
  money,
  relatedByTransactionId,
  relatedToTransaction,
  statusTone,
  structuredChecklistPhases,
  transactionCoordinatorEmail,
  transactionCoordinatorName,
  transactionTypeLabel
} from "./transactionReadOnly";

function StatePage({ eyebrow, title, body, tone = "default", onBack }) {
  return (
    <section className="readonly-state-page">
      <article className={`readonly-state-card readonly-state-card--${tone}`}>
        <div className="readonly-state-icon" aria-hidden="true">{tone === "danger" ? "!" : tone === "warning" ? "…" : "?"}</div>
        <span className="transactions-eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        <p>{body}</p>
        <button type="button" className="transactions-primary-action" onClick={onBack}>Return to Transactions</button>
      </article>
    </section>
  );
}

function Pill({ children, tone = "active" }) {
  return <span className={`readonly-pill readonly-pill--${tone}`}>{children}</span>;
}

function SummaryMetric({ label, value, detail, extra }) {
  return (
    <article className="readonly-summary-metric">
      <span>{label}</span>
      <strong>{value}</strong>
      {extra}
      {detail && <small>{detail}</small>}
    </article>
  );
}

function DefinitionList({ items }) {
  return (
    <dl className="readonly-definition-grid">
      {items.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value || "Unavailable"}</dd>
        </div>
      ))}
    </dl>
  );
}

function SectionCard({ title, children, wide = false }) {
  return (
    <section className={`readonly-section-card${wide ? " readonly-section-card--wide" : ""}`}>
      <h3>{title}</h3>
      {children}
    </section>
  );
}

function OverviewTab({ transaction }) {
  const verification = dateVerification(transaction);
  return (
    <div className="readonly-tab-stack">
      <div className="readonly-tab-intro">
        <div>
          <h2>Transaction Overview</h2>
          <p>Authoritative transaction information currently available from ROOTS.</p>
        </div>
        <Pill tone="outline">Read-only</Pill>
      </div>

      <div className="readonly-content-grid">
        <SectionCard title="Client Information">
          <DefinitionList items={[
            ["Client", transaction.clientName],
            ["Transaction ID", transaction.id],
            ["FUB Person ID", transaction.fubPersonId || "Unavailable"],
            ["Transaction type", transactionTypeLabel(transaction)],
            ["Intake status", transaction.intakeStatus]
          ]} />
        </SectionCard>

        <SectionCard title="Property and Status">
          <DefinitionList items={[
            ["Property or referral", transaction.propertyAddress],
            ["Current status", transaction.status],
            ["Transaction side", transaction.transactionSide],
            ["Workflow", transaction.transactionType]
          ]} />
        </SectionCard>

        <SectionCard title="Agreement and Key Dates" wide>
          <div className="readonly-date-grid">
            <article className="readonly-date-record readonly-date-record--unverified">
              <div><span>Date</span><strong>Closing or settlement</strong></div>
              <div><span>Value</span><strong>{formatDate(transaction.closingDate)}</strong></div>
              <div><span>Verification</span><Pill tone={verification.tone}>{verification.label}</Pill></div>
              <div><span>Source</span><strong>{transaction.closingDateSource || "Source metadata unavailable"}</strong></div>
            </article>
            <article className="readonly-date-record">
              <div><span>Date</span><strong>Contract</strong></div>
              <div><span>Value</span><strong>{formatDate(transaction.contractDate)}</strong></div>
              <div><span>Verification</span><Pill tone="muted">Status unavailable</Pill></div>
              <div><span>Source</span><strong>Production transaction record</strong></div>
            </article>
            <article className="readonly-date-record">
              <div><span>Date</span><strong>Listing</strong></div>
              <div><span>Value</span><strong>{formatDate(transaction.listingDate)}</strong></div>
              <div><span>Verification</span><Pill tone="muted">Status unavailable</Pill></div>
              <div><span>Source</span><strong>Production transaction record</strong></div>
            </article>
          </div>
        </SectionCard>

        <SectionCard title="Assigned Team Members">
          <DefinitionList items={[
            ["Assigned Agent", transaction.agentName],
            ["Agent identity", transaction.agentEmail],
            ["Assigned TC", transactionCoordinatorName(transaction)],
            ["TC identity", transactionCoordinatorEmail(transaction) || "Unavailable"]
          ]} />
        </SectionCard>

        <SectionCard title="Recorded Financial Terms">
          <DefinitionList items={[
            ["Sale or list price", transaction.salePrice ? money(transaction.salePrice) : "Unavailable"],
            ["Recorded expected GCI", transaction.adjustedGci ? money(transaction.adjustedGci) : "Unavailable"],
            ["Team gross", transaction.teamGross ? money(transaction.teamGross) : "Unavailable"],
            ["Calculation status", "No Command Center calculation performed"]
          ]} />
        </SectionCard>

        <SectionCard title="Important Notes" wide>
          <div className="readonly-notes-box">{transaction.notes || "No transaction notes are available in the current production record."}</div>
        </SectionCard>
      </div>
    </div>
  );
}

function phaseControlId(phaseId) {
  return `readonly-checklist-phase-${String(phaseId).replace(/[^a-zA-Z0-9_-]/g, "-")}`;
}

function ChecklistTab({ phases, expandedPhases, onTogglePhase }) {
  return (
    <div className="readonly-tab-stack">
      <div className="readonly-tab-intro">
        <div><h2>Checklist</h2><p>Authoritative checklist phases are displayed without completion, reopen, reassignment, or override controls.</p></div>
        <Pill tone="outline">Read-only checklist</Pill>
      </div>
      {phases === null ? (
        <div className="readonly-unavailable">
          <strong>Checklist phase data is unavailable.</strong>
          <p>ROOTS did not return authoritative structured phase fields for this transaction. No flat or inferred checklist is displayed.</p>
        </div>
      ) : phases.length ? (
        <div className="readonly-checklist-phases">
          {phases.map(phase => {
            const expanded = expandedPhases[phase.id] ?? true;
            const panelId = phaseControlId(phase.id);
            const completion = checklistPhaseCompletion(phase);
            return (
              <section className="readonly-checklist-phase" key={phase.id}>
                <button
                  type="button"
                  className="readonly-checklist-phase-trigger"
                  aria-expanded={expanded}
                  aria-controls={panelId}
                  onClick={() => onTogglePhase(phase.id)}
                >
                  <span className="readonly-checklist-phase-indicator" aria-hidden="true">⌄</span>
                  <span className="readonly-checklist-phase-copy">
                    <span>Phase</span>
                    <strong>{phase.name}</strong>
                  </span>
                  <span className="readonly-checklist-phase-progress">{completion.completed} of {completion.total} complete</span>
                </button>
                <div id={panelId} className="readonly-checklist-phase-panel" hidden={!expanded}>
                  <div className="readonly-task-list">
                    {phase.tasks.map(task => (
                      <article className="readonly-task-row" key={task.id}>
                        <div><span>Task</span><strong>{task.title}</strong></div>
                        <div><span>Owner</span><strong>{task.ownerName || "Not assigned"}</strong></div>
                        <div><span>Due</span><strong>{formatDate(task.dueDate)}</strong></div>
                        <div><span>Status</span><Pill tone={statusTone(task.status)}>{task.status || "Not set"}</Pill></div>
                      </article>
                    ))}
                  </div>
                </div>
              </section>
            );
          })}
        </div>
      ) : (
        <div className="readonly-unavailable"><strong>No checklist records are available.</strong><p>ROOTS did not return authoritative phased checklist records associated with this transaction.</p></div>
      )}
    </div>
  );
}

function DeadlinesTab({ transaction, deadlines }) {
  const verification = dateVerification(transaction);
  const records = [
    transaction.contractDate && {
      id: "contract",
      title: "Contract date",
      date: transaction.contractDate,
      type: "Contractual date",
      status: "Verification status unavailable",
      tone: "muted",
      source: "Production transaction record"
    },
    transaction.closingDate && {
      id: "closing",
      title: "Closing or settlement",
      date: transaction.closingDate,
      type: "Contractual date",
      status: verification.label,
      tone: verification.tone,
      source: transaction.closingDateSource || "Production transaction record"
    },
    ...deadlines.map(item => ({
      id: item.id,
      title: item.title,
      date: item.date,
      type: item.dateType || "Internal operational date",
      status: item.verificationStatus || "Internal operational date",
      tone: item.verificationStatus ? statusTone(item.verificationStatus) : "active",
      source: item.source || "ROOTS"
    }))
  ].filter(Boolean);

  return (
    <div className="readonly-tab-stack">
      <div className="readonly-tab-intro">
        <div><h2>Deadlines</h2><p>Dates are identified by source and verification status. No date can be edited here.</p></div>
        <Pill tone="outline">Read-only dates</Pill>
      </div>
      {records.length ? (
        <div className="readonly-date-grid">
          {records.map(item => (
            <article className={`readonly-date-record${item.tone === "danger" ? " readonly-date-record--unverified" : ""}`} key={item.id}>
              <div><span>Deadline</span><strong>{item.title}</strong></div>
              <div><span>Date</span><strong>{formatDate(item.date)}</strong></div>
              <div><span>Date type</span><strong>{item.type}</strong></div>
              <div><span>Verification</span><Pill tone={item.tone}>{item.status}</Pill></div>
              <div><span>Source</span><strong>{item.source}</strong></div>
            </article>
          ))}
        </div>
      ) : (
        <div className="readonly-unavailable"><strong>No deadline records are available.</strong><p>ROOTS did not return dates associated with this transaction.</p></div>
      )}
    </div>
  );
}

function DocumentsTab() {
  return (
    <div className="readonly-tab-stack">
      <div className="readonly-tab-intro">
        <div><h2>Documents</h2><p>Document information is limited to metadata made available by the production API.</p></div>
        <Pill tone="muted">Metadata only</Pill>
      </div>
      <div className="readonly-unavailable">
        <strong>Document metadata is not available in the current production response.</strong>
        <p>No upload, preview, download, replacement, approval, or deletion action is provided.</p>
      </div>
    </div>
  );
}

function FinancialsTab({ transaction }) {
  return (
    <div className="readonly-tab-stack">
      <div className="readonly-tab-intro">
        <div><h2>Financials</h2><p>Recorded transaction fields may be reviewed, but Command Center calculations and ledger actions are unavailable.</p></div>
        <Pill tone="muted">Unavailable</Pill>
      </div>
      <div className="readonly-unavailable readonly-unavailable--financial">
        <strong>Financial projection unavailable pending formula approval.</strong>
        <p>Official allocations, net commission, company dollar, cap treatment, commission posting, and ledger information are not calculated here.</p>
        <div className="readonly-terms-grid">
          <div><span>Recorded price</span><strong>{transaction.salePrice ? money(transaction.salePrice) : "Unavailable"}</strong></div>
          <div><span>Recorded expected GCI</span><strong>{transaction.adjustedGci ? money(transaction.adjustedGci) : "Unavailable"}</strong></div>
          <div><span>Recorded team gross</span><strong>{transaction.teamGross ? money(transaction.teamGross) : "Unavailable"}</strong></div>
        </div>
      </div>
    </div>
  );
}

function ActivityTab({ activity }) {
  return (
    <div className="readonly-tab-stack">
      <div className="readonly-tab-intro">
        <div><h2>Available Activity History</h2><p>Chronological activity returned by the current production data source.</p></div>
        <Pill tone="outline">Read-only history</Pill>
      </div>
      {activity.length ? (
        <div className="readonly-activity-list">
          {activity.map(item => (
            <article className="readonly-activity-item" key={item.id}>
              <span className="readonly-activity-dot" aria-hidden="true" />
              <div className="readonly-activity-time"><strong>{formatDateTime(item.createdAt || item.date)}</strong><small>{item.userName || item.userEmail || "ROOTS"}</small></div>
              <div className="readonly-activity-copy"><strong>{item.action || item.title || "Activity"}</strong><p>{item.details || "No additional activity detail is available."}</p></div>
            </article>
          ))}
        </div>
      ) : (
        <div className="readonly-unavailable"><strong>No activity history is available.</strong><p>The current production bootstrap does not return transaction-level activity records.</p></div>
      )}
    </div>
  );
}

function SellerMarketingTab() {
  return (
    <div className="readonly-tab-stack">
      <div className="readonly-tab-intro">
        <div><h2>Seller Marketing</h2><p>Marketing information is shown only when authoritative approved data becomes available.</p></div>
        <Pill tone="muted">Unavailable</Pill>
      </div>
      <div className="readonly-unavailable"><strong>Seller Marketing unavailable pending approved data.</strong><p>No client-side budget calculation, approval, expense entry, receipt upload, or funding request is included.</p></div>
    </div>
  );
}

export default function TransactionCommandCenter({
  transaction,
  transactionId,
  user,
  tasks = [],
  activity = [],
  calendar = [],
  checklistPhaseDataAvailable = false,
  onBack
}) {
  const [activeTab, setActiveTab] = useState("overview");
  const [expandedChecklistPhases, setExpandedChecklistPhases] = useState({});
  const tabRefs = useRef([]);

  const relatedTasks = useMemo(
    () => tasks.filter(item => relatedByTransactionId(item, transaction)),
    [tasks, transaction]
  );
  const relatedActivity = useMemo(
    () => activity.filter(item => relatedToTransaction(item, transaction)),
    [activity, transaction]
  );
  const relatedDeadlines = useMemo(
    () => calendar.filter(item => relatedByTransactionId(item, transaction)),
    [calendar, transaction]
  );
  const checklistPhases = useMemo(
    () => structuredChecklistPhases(relatedTasks, checklistPhaseDataAvailable),
    [checklistPhaseDataAvailable, relatedTasks]
  );

  const category = categoryFor(transaction);
  const tabs = useMemo(() => {
    const items = ["overview", "checklist", "deadlines", "documents", "financials", "activity"];
    if (category === "sellers") items.splice(5, 0, "seller marketing");
    return items;
  }, [category]);

  useEffect(() => {
    if (!tabs.includes(activeTab)) setActiveTab("overview");
  }, [activeTab, tabs]);

  useEffect(() => {
    setExpandedChecklistPhases({});
  }, [transaction?.id]);

  if (!transaction) {
    return <StatePage eyebrow="Transactions" title="Transaction not found." body={`The transaction ${transactionId || "requested"} could not be found or is not available to the current user.`} onBack={onBack} />;
  }

  if (!canViewTransaction(user, transaction)) {
    return <StatePage eyebrow="Access denied" title="You cannot view this transaction." body="No property, client, financial, document, deadline, checklist, or activity information has been displayed." tone="danger" onBack={onBack} />;
  }

  if (!isApproved(transaction)) {
    return <StatePage eyebrow="Transaction approval required" title="Transaction awaiting approval." body="The read-only Command Center remains unavailable until the existing Transactions Overview approval workflow is complete." tone="warning" onBack={onBack} />;
  }

  const verification = dateVerification(transaction);
  const tcName = transactionCoordinatorName(transaction);

  function changeTab(nextIndex) {
    const index = (nextIndex + tabs.length) % tabs.length;
    setActiveTab(tabs[index]);
    requestAnimationFrame(() => tabRefs.current[index]?.focus());
  }

  function onTabKeyDown(event, index) {
    if (event.key === "ArrowRight") { event.preventDefault(); changeTab(index + 1); }
    if (event.key === "ArrowLeft") { event.preventDefault(); changeTab(index - 1); }
    if (event.key === "Home") { event.preventDefault(); changeTab(0); }
    if (event.key === "End") { event.preventDefault(); changeTab(tabs.length - 1); }
  }

  function toggleChecklistPhase(phaseId) {
    setExpandedChecklistPhases(current => ({
      ...current,
      [phaseId]: !(current[phaseId] ?? true)
    }));
  }

  function renderTab() {
    if (activeTab === "checklist") {
      return (
        <ChecklistTab
          phases={checklistPhases}
          expandedPhases={expandedChecklistPhases}
          onTogglePhase={toggleChecklistPhase}
        />
      );
    }
    if (activeTab === "deadlines") return <DeadlinesTab transaction={transaction} deadlines={relatedDeadlines} />;
    if (activeTab === "documents") return <DocumentsTab />;
    if (activeTab === "financials") return <FinancialsTab transaction={transaction} />;
    if (activeTab === "activity") return <ActivityTab activity={relatedActivity} />;
    if (activeTab === "seller marketing") return <SellerMarketingTab />;
    return <OverviewTab transaction={transaction} />;
  }

  return (
    <section className="readonly-command-center">
      <header className="readonly-command-header">
        <button type="button" className="readonly-back-button" onClick={onBack}>← Back to Transactions</button>
        <div className="readonly-command-heading">
          <div>
            <span className="transactions-eyebrow">{transactionTypeLabel(transaction)} transaction</span>
            <h1>{transaction.propertyAddress || "Transaction"}</h1>
            <div className="readonly-command-meta">
              <span><strong>{transaction.clientName || "Client unavailable"}</strong></span>
              <span>Agent: {transaction.agentName || "Not assigned"}</span>
              <span>TC: {tcName}</span>
              <Pill tone={statusTone(transaction.status)}>{transaction.status || "Not set"}</Pill>
              <span>Closing: <strong>{formatDate(transaction.closingDate)}</strong> <Pill tone={verification.tone}>{verification.label}</Pill></span>
            </div>
          </div>
          <Pill tone="outline">Read-only</Pill>
        </div>
      </header>

      <main className="readonly-detail-content">
        {verification.label === "Unverified" && transaction.closingDate && (
          <div className="readonly-attention-banner">
            <span aria-hidden="true">!</span>
            <div><strong>Unverified closing or settlement date</strong><p>The displayed date has no approved verification status in the current production record.</p></div>
          </div>
        )}

        <section className="readonly-summary-grid" aria-label="Transaction summary">
          <SummaryMetric label="Status" value={transaction.status || "Not set"} detail={transaction.intakeStatus || "Intake status unavailable"} />
          <SummaryMetric label="Transaction type" value={transactionTypeLabel(transaction)} detail={transaction.transactionType || transaction.transactionSide || "Classification unavailable"} />
          <SummaryMetric label="Closing or settlement" value={formatDate(transaction.closingDate)} detail={transaction.closingDateSource || "Source metadata unavailable"} extra={<Pill tone={verification.tone}>{verification.label}</Pill>} />
          <SummaryMetric label="Assigned Agent" value={transaction.agentName || "Not assigned"} detail={transaction.agentEmail || "Identity unavailable"} />
          <SummaryMetric label="Assigned TC" value={tcName} detail={transactionCoordinatorEmail(transaction) || "Identity unavailable"} />
          <SummaryMetric
            label="Checklist"
            value={checklistPhases === null ? "Unavailable" : `${relatedTasks.length} task${relatedTasks.length === 1 ? "" : "s"}`}
            detail={checklistPhases === null ? "Authoritative structured phase data not returned" : "Authoritative phased records returned by ROOTS"}
          />
        </section>

        <section className="readonly-command-card">
          <div className="readonly-detail-tabs" role="tablist" aria-label="Transaction Command Center sections">
            {tabs.map((tab, index) => (
              <button
                key={tab}
                ref={element => { tabRefs.current[index] = element; }}
                type="button"
                role="tab"
                aria-selected={activeTab === tab}
                aria-controls={`readonly-panel-${tab.replace(/\s+/g, "-")}`}
                id={`readonly-tab-${tab.replace(/\s+/g, "-")}`}
                tabIndex={activeTab === tab ? 0 : -1}
                className={activeTab === tab ? "active" : ""}
                onClick={() => setActiveTab(tab)}
                onKeyDown={event => onTabKeyDown(event, index)}
              >
                {tab}
              </button>
            ))}
          </div>
          <div
            className="readonly-tab-content"
            role="tabpanel"
            tabIndex="0"
            id={`readonly-panel-${activeTab.replace(/\s+/g, "-")}`}
            aria-labelledby={`readonly-tab-${activeTab.replace(/\s+/g, "-")}`}
          >
            {renderTab()}
          </div>
        </section>
      </main>
    </section>
  );
}
